// The canvas's empty state: what Sayer does, a place to ask, and worked examples. Submitting
// goes through the same handler as the chat composer. Pure props.
import React, { useEffect, useRef, useState } from "react";
import { C } from "../lib/colors.js";
import { TIER_ORDER, tagOwner, effectiveTier } from "../lib/forecast/engines.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import TierSelector from "./TierSelector.jsx";
import FeaturedForecasts from "./FeaturedForecasts.jsx";
import { useFeaturedSearch } from "../hooks/useFeaturedSearch.js";
import { Button, Kicker } from "./ui/Primitives.jsx";
import { IconArrowRight, IconCalendar, IconInfo } from "./ui/Icons.jsx";

// Full, answerable questions with the date they resolve, grouped by the desks that ask them.
const EXAMPLE_GROUPS = [
  {
    domain: "Rates & macro",
    items: [
      {
        q: "Will the ECB cut its deposit facility rate before April 1, 2027?",
        resolves: "2027-04-01",
      },
      {
        q: "Will the upper bound of the Fed's target range be at or below 3.25% on June 30, 2027?",
        resolves: "2027-06-30",
      },
    ],
  },
  {
    domain: "Credit & risk",
    items: [
      {
        q: "Will the ICE BofA US High Yield spread close above 500 bp on any day before July 1, 2027?",
        resolves: "2027-07-01",
      },
      {
        q: "Will Moody's downgrade France's sovereign credit rating before March 31, 2027?",
        resolves: "2027-03-31",
      },
    ],
  },
  {
    domain: "Policy & geopolitics",
    items: [
      {
        q: "Will Russia and Ukraine agree to a ceasefire before July 1, 2027?",
        resolves: "2027-07-01",
      },
      {
        q: "Will China hold a named large-scale military exercise around Taiwan before April 1, 2027?",
        resolves: "2027-04-01",
      },
    ],
  },
  {
    domain: "Energy & commodities",
    items: [
      {
        q: "Will Brent crude settle above $90 a barrel on any day before June 30, 2027?",
        resolves: "2027-06-30",
      },
      {
        q: "Will OPEC+ announce a cut to its production targets before April 1, 2027?",
        resolves: "2027-04-01",
      },
    ],
  },
];

/** Each example question's place in the list: its telemetry key (never its text). */
const EXAMPLE_INDEX = new Map(EXAMPLE_GROUPS.flatMap((g) => g.items).map((it, i) => [it.q, i]));

/** The ask box's depth picker, and where its choice is made (`chat.tier_change`). */
const ASK_TIER_IDS = {
  toggle: ui("ask-hero.tier-toggle"),
  menu: ui("ask-hero.tier-menu"),
  option: ui("ask-hero.tier-option"),
};
const ASK_TIER_INTENT = { origin: "ask-hero.tier-option", surface: "ask-hero" };

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const HERO_CSS = `
.ask-hero-card{appearance:none;display:flex;flex-direction:column;gap:6px;width:100%;text-align:left;font:inherit;cursor:pointer;background:var(--c-panel);border:1px solid var(--c-border);border-radius:8px;padding:10px 12px;color:var(--c-white);transition:border-color .12s ease,background .12s ease;}
.ask-hero-card:hover{border-color:var(--c-purpleDim);background:var(--c-rowInactive);}
.ask-hero-card:focus-visible{outline:2px solid var(--c-accent);outline-offset:2px;}
.ask-hero-card[aria-pressed="true"]{border-color:var(--c-accent);}
.ask-hero-input::placeholder{color:var(--c-gray);}
.ask-hero-examples{container-type:inline-size;}
.ask-hero-groups{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px 24px;}
@container (min-width: 780px){.ask-hero-groups{grid-template-columns:repeat(4,minmax(0,1fr));gap:16px 16px;}}
@container (max-width: 520px){.ask-hero-groups{grid-template-columns:1fr;}}
`;

/**
 * @param {object} props
 * @param {(text: string, opts: {example: string|null}) => void} props.onSubmit  asks the
 *   question (useAskHeroSubmit); `example` is the example question last picked
 * @param {string|null} props.forecastEngine
 * @param {string[]} props.orchestrators  the live engine roster
 * @param {(tier: string, at?: {origin: string, surface: string}) => void} props.onSelectTier
 * @param {boolean} props.depthSelectorEnabled  the Quick/Deep selector flag
 * @param {string|number} [props.leftInset]  the left panel's width, so the hero centres in the
 *   visible canvas
 * @param {object[]} [props.featured]  Today's cards; none shows the examples
 * @param {object[]} [props.searchPool]  every question the cards may switch to as the user types
 * @param {(item: object, at?: {slot: number}) => void} [props.onOpenFeatured]
 * @param {{text: string, nonce: number}|null} [props.prefill]  text to put in the ask box
 * @param {() => void} [props.onPrefillUsed]
 * @param {{text: string, question: string}|null} [props.notice]  why the last question asked here
 *   did not become a forecast; its question goes back in the box to adjust
 * @param {() => void} [props.onNoticeSeen]  the user has started editing
 */
export default function AskHero({
  onSubmit,
  forecastEngine,
  orchestrators = [],
  onSelectTier,
  depthSelectorEnabled,
  leftInset = 0,
  featured = [],
  searchPool = [],
  onOpenFeatured,
  prefill = null,
  onPrefillUsed,
  notice = null,
  onNoticeSeen,
}) {
  const [text, setText] = useState(() => notice?.question || "");
  const [focused, setFocused] = useState(false);
  // Up to four cards follow what is typed.
  const shownFeatured = useFeaturedSearch({ cards: featured, pool: searchPool, text });
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  // The example question last put in the box: asked unchanged, the ask is credited to it.
  const exampleRef = useRef(null);
  // A question that did not become a forecast comes back to the box, ready to adjust.
  useEffect(() => {
    if (notice?.question) setText((cur) => cur || notice.question);
  }, [notice]);
  // "Make it my own" lands here with the question in the box, ready to adjust and ask.
  useEffect(() => {
    if (!prefill?.text) return;
    pick(prefill.text);
    onPrefillUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);
  const tiers = TIER_ORDER.filter((t) => {
    const o = tagOwner(t);
    return o && orchestrators.includes(o);
  });
  const showDepth = !!depthSelectorEnabled && tiers.length > 0;
  const ready = !!forecastEngine;
  const canSubmit = ready && text.trim().length > 0;

  const submit = () => {
    if (!canSubmit) {
      // Always full lavender, as the live send button is; an empty ask just puts the cursor back.
      inputRef.current?.focus();
      return;
    }
    const q = text.trim();
    setText("");
    onSubmit(q, { example: exampleRef.current });
    exampleRef.current = null;
  };
  const pick = (q) => {
    setText(q);
    // Focus with the caret at the end, so the user can adjust the example before asking.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(q.length, q.length);
    });
  };

  return (
    <div
      data-testid="ask-hero"
      {...surface("ask-hero")}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: leftInset,
        right: 0,
        zIndex: 35,
        // Moves with the rail panel, not ahead of it.
        transition: "left var(--rail-slide)",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        padding: "32px 40px 24px",
        boxSizing: "border-box",
        background: `color-mix(in srgb, ${C.bg} 97%, transparent)`,
        cursor: "default",
        userSelect: "text",
        animation: "simpleFadeIn 0.3s ease-out",
      }}
    >
      <style>{HERO_CSS}</style>
      <div style={{ width: "100%", maxWidth: 960, margin: "auto" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 32,
            lineHeight: 1.2,
            fontWeight: 600,
            color: C.white,
          }}
        >
          What would you like to forecast?
        </h1>

        <div
          ref={boxRef}
          style={{
            position: "relative",
            marginTop: 24,
            background: C.inputBg,
            border: `1px solid ${focused ? C.accent : C.border}`,
            transition: "border-color .12s ease",
          }}
        >
          <span
            aria-hidden="true"
            className="sy-num"
            style={{
              position: "absolute",
              left: 16,
              top: 14,
              fontSize: 18,
              lineHeight: "24px",
              color: C.accent,
              pointerEvents: "none",
            }}
          >
            ›
          </span>
          <label htmlFor="ask-hero-input" style={{ position: "absolute", left: -9999 }}>
            Ask Sayer
          </label>
          <textarea
            {...ui("ask-hero.input")}
            id="ask-hero-input"
            ref={inputRef}
            className="ask-hero-input"
            value={text}
            rows={2}
            onChange={(e) => {
              setText(e.target.value);
              if (notice) onNoticeSeen?.();
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Ask a question about the future, e.g. Will the Fed cut rates before June 30, 2027?"
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              resize: "none",
              border: "none",
              outline: "none",
              background: "transparent",
              color: C.white,
              font: "inherit",
              fontSize: 15,
              lineHeight: 1.5,
              padding: "16px 16px 8px 38px",
              minHeight: 76,
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 8px 8px 8px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
              {showDepth && (
                <TierSelector
                  tiers={tiers}
                  current={effectiveTier(forecastEngine)}
                  onSelect={(t) => onSelectTier(t, ASK_TIER_INTENT)}
                  C={C}
                  align="start"
                  placement="below"
                  ids={ASK_TIER_IDS}
                />
              )}
            </div>
            <Button
              {...ui("ask-hero.submit")}
              variant="primary"
              aria-disabled={!canSubmit}
              title={ready ? undefined : "Connecting to Sayer…"}
              onClick={submit}
            >
              Ask Sayer{" "}
              <span aria-hidden="true" className="sy-num" style={{ fontSize: 14 }}>
                ↵
              </span>
            </Button>
          </div>
        </div>
        {!ready && (
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: C.gray,
            }}
          >
            <IconInfo width={14} height={14} />
            Connecting to Sayer…
          </div>
        )}
        {notice?.text && (
          <div
            role="status"
            data-testid="ask-hero-notice"
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 13,
              lineHeight: 1.5,
              color: C.grayL,
            }}
          >
            <IconInfo width={14} height={14} style={{ flex: "none", marginTop: 3 }} />
            <span style={{ whiteSpace: "pre-wrap" }}>{notice.text}</span>
          </div>
        )}

        {featured.length > 0 && onOpenFeatured ? (
          <div className="ask-hero-examples" style={{ marginTop: 72 }}>
            <FeaturedForecasts items={shownFeatured} onOpen={onOpenFeatured} />
          </div>
        ) : (
          <div className="ask-hero-examples" style={{ marginTop: 72 }}>
            <Kicker>Examples</Kicker>
            <div className="ask-hero-groups" style={{ marginTop: 12 }}>
              {EXAMPLE_GROUPS.map((g) => (
                <div key={g.domain}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.grayL, marginBottom: 8 }}>
                    {g.domain}
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {g.items.map((it) => (
                      <button
                        {...ui("ask-hero.example", EXAMPLE_INDEX.get(it.q))}
                        key={it.q}
                        type="button"
                        className="ask-hero-card"
                        aria-pressed={text.trim() === it.q}
                        title="Use this question"
                        onClick={() => {
                          exampleRef.current = it.q;
                          pick(it.q);
                        }}
                      >
                        <span style={{ fontSize: 13, lineHeight: 1.45 }}>{it.q}</span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            color: C.gray,
                          }}
                        >
                          <IconCalendar width={13} height={13} />
                          Resolves {fmtDate(it.resolves)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
